import constants from '../constants';
import axios from 'axios';
import { FormJson } from 'edocatbase/Form';
import { Selectable } from '../types/types';

export const fetchWorkflowData = (workflowId: string) => ({
	type: constants.FETCH_WORKFLOW_DATA,
	payload: {
		promise: fetch(`${window.appContext}/proxy/alfresco/emvc/wf/wfInfo?workflowId=${workflowId}&${moment().unix()}`, {
			credentials: 'same-origin',
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const fetchTaskForm = (taskId: string, mode: string, stateToMerge: FormJson | null = null) => ({
	type: constants.FETCH_TASK_FORM,
	payload: {
		promise: fetch(
			`${
				window.appContext
			}/page/edocat/edocatforms/forms?itemKind=task&itemId=${taskId}&mode=${mode}&${moment().unix()}`,
			{
				credentials: 'same-origin',
			}
		)
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.then(response => {
				return {
					form: response,
					stateToMerge,
				};
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const setFile = (file: Selectable) => ({
	type: constants.SET_FILE,
	payload: {
		file,
	},
});

export const getUserDevice = (navigator:any) => ({
	type: constants.GET_USER_DEVICE,
	payload: navigator,
  })

export const resetState = () => ({
	type: constants.RESET_STATE,
});

export const fetchDocMeta = (nodeRef: String) => ({
	type: constants.FETCH_DOCUMENT_META,
	payload: {
		promise: fetch(
			`${window.appContext}/page/edocat/edocatforms/forms?itemKind=node&itemId=${nodeRef}&mode=view&${moment().unix()}`,
			{
				credentials: 'same-origin',
			}
		)
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.then(response => ({
				response,
				nodeRef,
			}))
			.catch(reason => {
				throw reason;
			}),
	},
});

export const fetchDocMetaEdit = (nodeRef: String) => ({
	type: constants.FETCH_DOCUMENT_META_EDIT,
	payload: {
		promise: fetch(
			`${
				window.appContext
			}/page/edocat/edocatforms/forms?itemKind=node&itemId=${nodeRef}&mode=edit&showSubmitButton=false&${moment().unix()}`,
			{
				credentials: 'same-origin',
			}
		)
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const saveDocMeta = (nodeRef: String, data: any) => {
	const token = encodeURIComponent(window.Alfresco.util.CSRFPolicy.getToken());
	return {
		type: constants.PROMISE_SAVE_DOC_META,
		payload: fetch(
			`${window.appContext}/proxy/alfresco/emvc/folder/rename?nodeRef=${nodeRef}`,
			{
				credentials: 'same-origin',
				body: JSON.stringify(data),
				method: 'POST',
				headers: {
					[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
					'Content-Type': 'application/json',
				},
			}
		)
			.then(response => {
				if (response.status !== 200) {
					return response.json().then(res => {
						throw new Error(res.message);
					});
				}
				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	};
};

export const fetchFiles = (items: string[]) => ({
	type: constants.FETCH_FILES,
	payload: fetch(`${window.appContext}/proxy/alfresco/api/forms/picker/items`, {
		credentials: 'same-origin',
		method: 'POST',
		body: JSON.stringify({
			itemValueType: 'nodeRef',
			items,
		}),
		headers: {
			[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
			'Content-Type': 'application/json',
		},
	})
		.then(response => {
			if (response.status !== 200) {
				throw response.json();
			}

			return response.json();
		})
		.catch(reason => {
			throw reason;
		}),
});

export const uploadPackageItems = (formData: FormData, onProgress: Function) => {
	const token = encodeURIComponent(window.Alfresco.util.CSRFPolicy.getToken());
	return {
		type: constants.PROMISE_UPLOAD_PACKAGE_ITEMS,
		payload: axios({
			method: 'post',
			url: `${
				window.appContext
			}/proxy/alfresco/edocat/task/PackageItemsUploadPost?${window.Alfresco.util.CSRFPolicy.getParameter()}=${token}`,
			data: formData,
			headers: {
				[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
            },
			onUploadProgress: event => onProgress(event),
		})
			.then(response => {
				if (response.status !== 200) {
					throw response.data;
				}

				return {
					...response.data,
				};
			})
			.catch(reason => {
				throw reason?.response?.data?.message || 'Error';
			}),
	};
};

export const onFormSubmit = (data: any, url: string) => ({
	type: constants.PROMISE_ON_FORM_SUBMIT,
	payload: {
		promise: fetch(url, {
			credentials: 'same-origin',
			body: JSON.stringify(data),
			method: 'POST',
			headers: {
				[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
				'Content-Type': 'application/json',
			},
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const setAssignee = (taskId: string, userName: string, type: string) => ({
	type: constants[`PROMISE_${type}_ASSIGNEE`],
	payload: {
		promise: fetch(`${window.appContext}/proxy/alfresco/api/task-instances/${taskId}`, {
			credentials: 'same-origin',
			method: 'PUT',
			headers: {
				[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				cm_owner: userName,
			}),
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const changeAssignee = (body: JSON) => ({
	type: constants.PROMISE_CHANGE_ASSIGNEE,
	payload: {
		promise: fetch(`${window.appContext}/proxy/alfresco/emvc/task/setAssignee`, {
			credentials: 'same-origin',
			method: 'POST',
			headers: {
				[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}
				// domyslet smysluplny response
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const changeAssigneePoolTask = (body: JSON) => ({
	type: constants.PROMISE_CHANGE_ASSIGNEE_POOL_TASK,
	payload: {
		promise: fetch(`${window.appContext}/proxy/alfresco/emvc/task/setPoolAssignee`, {
			credentials: 'same-origin',
			method: 'POST',
			headers: {
				[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(body),
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}
				// domyslet smysluplny response
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const fetchDocumentData = (node: string) => ({
	type: constants.FETCH_DOCUMENT_DATA,
	payload: {
		promise: fetch(`${window.appContext}/proxy/alfresco/api/metadata?nodeRef=${node}&time=${moment().unix()}`, {
			credentials: 'same-origin',
		})
			.then(response => {
				if (response.status !== 200) {
					throw new Error(response.statusText);
				}

				return response.json();
			})
			.catch(reason => {
				throw reason;
			}),
	},
});

export const onTaskSubmitStart = () => ({
	type: constants.TASK_SUBMIT_START,
});

export const onTaskSubmitSuccess = () => ({
	type: constants.TASK_SUBMIT_SUCCESS,
});

export const setOutcomeButton = (value: string) => ({
	type: constants.SET_OUTCOME_BUTTON,
	payload: value,
});

export const cancelWorkflow = (workflowId: string, cancelMassage: string, notification: string) => ({
	type: constants.PROMISE_CANCEL_WORKFLOW,
	payload: axios({
		method: 'DELETE',
		url: `${window.appContext}/proxy/alfresco/emvc/wf/wfCancel?cancelMessage=${cancelMassage}&notify=${notification}&workflowId=${workflowId}`,
		headers: {
			[window.Alfresco.util.CSRFPolicy.getHeader()]: window.Alfresco.util.CSRFPolicy.getToken(),
		},
	})
		.then(response => {
			if (response.status !== 200) {
				throw new Error(response.statusText);
			}

			return response.data;
		})
		.catch(reason => {
			throw reason;
		}),
});
